import { Dropdown } from 'flowbite-react';
import { LoadingCircle } from './Loading';
import { useOwnProfileQuery } from '../hooks/useOwnProfileQuery';
import Profile from '../assets/profile.svg';
import Temp from '../assets/profile.svg';
import React, { useEffect, useRef, useState } from 'react';
import { v4 as uuid4 } from 'uuid';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { uploadAvatar } from '../services/users';
import { removePushToken, sendPushToken } from "../services/notifications";
import { deleteFirebaseToken, getFirebaseToken } from "../firebase";
import { useUserContext } from "../context/user";
import { RoundSlider } from "./RoundSlider";
import { useAlert } from "../context/alert";
import { useSignOut } from "../hooks/useSignOut"

export const ProfileDropDown = () => {
    const [inputKey, setInputKey] = useState(uuid4());
    const queryClient = useQueryClient();
    const inputFile = useRef<HTMLInputElement | null>(null);
    const { setError } = useAlert();
    const { jwtToken, jwtRefreshToken, setNotificationsEnabled, notificationsEnabled, setUserUuid } = useUserContext();
    const {
        data: profile,
        isLoading,
        isSuccess,
    } = useOwnProfileQuery();
    const signOut = useSignOut();

    const invalidateProfile = () => {
        queryClient.invalidateQueries({
            queryKey: ['ownProfile'],
            refetchType: 'all',
        });
    }

    const { mutate: updateAvatar } = useMutation({
        mutationFn: uploadAvatar,
        onSuccess: (data) => {
            invalidateProfile();
            setInputKey(uuid4());
        }
    });
    const { mutate: deletePushToken } = useMutation({
        mutationFn: removePushToken,
        onSuccess: (data) => {
            setNotificationsEnabled(false)
        }
    });
    const { mutate: addPushToken } = useMutation({
        mutationFn: ({ token, jwt }: { token: string, jwt: string }) => sendPushToken(token, jwt),
        onSuccess: (data) => {
            setNotificationsEnabled(true)
        }
    });

    const enableNotifications = () => {
        Notification.requestPermission().then((permission) => {
            if (permission === 'granted') {
                // Запрашиваем токен
                getFirebaseToken().then(token => {
                    // Если токен есть, то отправляем его на бэк
                    if (token) {
                        addPushToken({ token, jwt: jwtToken || '' });
                    }
                })
            } else {
                console.log('Unable to get permission to notify.');
                setError('Уведомления запрещены. Чтобы получать уведомления, разрешите их в браузере.')
            }
        }).catch((err) => {
            console.log('Error requesting notification permission.', err);
            setError('Ошибка запроса разрешений для уведомлений.')
        });

    }
    const disableNotifications = () => {
        // удаляем именно зарегистрированный токен а не свежий из getToken иначе на бэке его нет
        const token = localStorage.getItem("xFcmToken");
        if (token) {
            deletePushToken(token);
        } else {
            setNotificationsEnabled(false);
        }
        deleteFirebaseToken().then();  // отписка в firebase
    }


    const handleNotifications = (value: boolean) => {
        if (value) {
            enableNotifications()
        } else {
            disableNotifications()
        }
    }

    const readFile = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            updateAvatar(e.target.files[0]);
        }
    }

    // Загружаем пользователя и проставляем в контекст uuid пользователя
    useEffect(() => {
        if (isSuccess) {
            setUserUuid(profile?.pk)
            if (notificationsEnabled == null) {
                // Уведомления не были установлены (после logout), вкл по умолчанию
                console.log('Set notification for the first time')
                handleNotifications(true)
            }
        }
    }, [profile]);

    if (isLoading) return <LoadingCircle />;

    const getProfileBrandPost = (): string | null => {
        const { brand, post } = profile || {};

        let val = brand || '';
        if (post) {
            val += `, ${post}`;
        }

        return (val && val.length > 0) ? val : null;
    };

    if (isSuccess) return (
        <Dropdown
            className='w-[435px] rounded-lg z-20'
            arrowIcon={false}
            inline
            dismissOnClick={false}
            label={
                <img className='bg-white p-3 rounded-lg' src={Profile} alt='profile' />
            }
        >
            <Dropdown.Header>
                <div className='fixed bg-melon-yellow rounded left-1 top-1 right-1 h-[101px] -z-10'></div>
                <div className='text-center'>
                    <img
                        className='rounded-[50%] mx-auto h-[100px] w-[100px] object-cover object-center mt-[30px] mb-3
                            border-4 border-white shadow-lg shadow-melon-light bg-melon-white'
                        src={profile.photo_img?.file ?? Temp}
                        alt='avatar'
                    />
                    <div
                        className='text-[16px] underline cursor-pointer'
                        onClick={() => {
                            if (inputFile !== null && inputFile.current !== null) {
                                inputFile.current.click()
                            }
                        }}
                    >
                        Загрузить фото
                    </div>
                    <div>
                        <input
                            hidden
                            ref={inputFile}
                            className=''
                            type='file'
                            onChange={readFile}
                            key={inputKey}
                        />
                    </div>
                    <div
                        className='text-[20px] mt-7'>{profile.last_name} {profile.first_name} {profile.middle_name}</div>
                    <div className='text-melon-grey-darker text-sm mt-[10px]'>{getProfileBrandPost()}</div>
                </div>

                <div className='text-melon-grey-darker text-sm mt-7'>Телефон</div>
                <div className='text-[16px]'>{profile.phone}</div>

                <div className='text-melon-grey-darker text-sm mt-7'>E-mail адрес</div>
                <div className='text-[16px]'>{profile.email}</div>
            </Dropdown.Header>
            <Dropdown.Item
                className='text-md justify-between'
            >
                <span>Уведомления</span>
                <RoundSlider value={notificationsEnabled ?? false} onChange={handleNotifications} />
            </Dropdown.Item>
            <Dropdown.Divider />
            {/* NOTE: Disable layout  */}
            {/* <Dropdown.Item
                className='text-[16px] text-red-500'
                onClick={() => {
                    window.location.replace('/layout')
                }}
            >
                Сменить сервис
            </Dropdown.Item> */}
            <Dropdown.Divider />
            <Dropdown.Item
                className='text-[16px] text-red-500'
                onClick={signOut}
            >
                Выйти
            </Dropdown.Item>
        </Dropdown>
    )

    return <img className='bg-white p-3 rounded-lg' src={Profile} alt='profile' />
};
